/**
 * T-008 — Position / occurrence resolver.
 *
 * Bridges the pure op model to the document. Each `delete`/`insert` op must resolve to a concrete
 * location so the executor can act with a plain `Range.search` and no further diffing. The deep-dive
 * chose a **running char-offset pointer**: walk the ops in baseline order carrying a cursor over the
 * baseline string; `keep`/`delete` advance it by `value.length`, `insert` does not.
 *
 *  - For a `delete`, the target is the `occurrenceIndex`-th match of its value — where
 *    `occurrenceIndex` is how many identical matches start *before* the cursor. `Range.search`
 *    returns matches in document order, so the executor picks results[occurrenceIndex].
 *  - For an `insert`, we resolve an **anchor**: the run of baseline text immediately before the
 *    insertion point (bounded, so it stays a legal search term), plus that anchor's own occurrence
 *    index. The executor searches the anchor and inserts after it. At the paragraph start the anchor
 *    is `{ type: 'start' }`.
 *
 * This is the piece that drifted in the old approach. It is safe now **only** because T-001 proved
 * the baseline is the same coordinate space `Range.search` walks. Kept pure (no Word call — that is
 * T-009/T-010); occurrence math is unit-tested against repeated-word strings.
 *
 * Protected regions (T-007): a `delete` whose span, or an `insert` whose point, touches a field or
 * bookmark is flagged `protected: true`. It is NOT applied here — it feeds the pre-flight abort
 * (T-009), so the engine never mutates a field.
 *
 * ## Bounds & deferred concerns
 *  - Search values are kept within {@link MAX_SEARCH_LEN} (well under Word's 255-char search limit).
 *    A `delete` run longer than the cap is **sliced** here into consecutive searchable pieces (see
 *    `sliceForSearch`), each a located delete at its own offset that pre-flight/execution handle
 *    independently — so a long run applies rather than aborting. Insert anchors are capped when
 *    built. `oversize` therefore stays false for real ops and is only a defensive backstop.
 *  - Insert anchoring uses preceding text with a `start` fallback. Anchoring on *following* text
 *    when the preceding run is unusable (e.g. an insert immediately after a field) is a documented
 *    T-010 refinement; such an insert is still flagged so pre-flight can decide.
 */

import { Op } from './types';
import { ProtectedRegionSet } from './protectedRegions';
import { logQuickResponseBidiCompare } from '../../utils/quickResponseDebug';

/** Max length of a value handed to Range.search (Word's hard limit is 255). */
export const MAX_SEARCH_LEN = 200;

/**
 * Can this value be passed to `Range.search` without Word rejecting it
 * (`SearchStringInvalidOrTooLong`)? Pure.
 *
 * Word rejects an empty string, anything over 255 chars, and strings containing control characters
 * — notably paragraph/line breaks (CR, LF, VT, FF), field-result markers (0x13–0x15), and the
 * embedded-object anchor (0xFFFC), all of which can appear in a live legal paragraph's `range.text`.
 * Tab (0x09) is allowed. An op whose value is unsearchable becomes a clean pre-flight failure
 * instead of a thrown search.
 */
export function isWordSearchable(value: string): boolean {
  if (value.length === 0 || value.length > 255) return false;
  for (let i = 0; i < value.length; i++) {
    if (isUnsearchableCharCode(value.charCodeAt(i))) return false;
  }
  return true;
}

/** Indent tab / cell marker / spaces — Word search cannot reliably find these alone. */
export function isFormattingOnlyDelete(value: string): boolean {
  return value.length > 0 && /^[\s\u0007]+$/.test(value);
}

/** A single character code Word's search rejects (control char, tab excepted, or object anchor). */
function isUnsearchableCharCode(code: number): boolean {
  const isControl = (code < 0x20 && code !== 0x09) || (code >= 0x7f && code <= 0x9f);
  return isControl || code === 0xfffc;
}

/**
 * The distinct character codes in `value` that make it unsearchable (control chars / object anchor),
 * for diagnostics. Codes only — no text. Empty if the value is searchable.
 */
export function unsearchableCharCodesOf(value: string): number[] {
  const codes = new Set<number>();
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (isUnsearchableCharCode(code)) codes.add(code);
  }
  return [...codes].sort((a, b) => a - b);
}

export type InsertAnchor =
  /** Insert at the paragraph start — only when the insertion is genuinely at offset 0. */
  | { type: 'start' }
  /** Insert at the paragraph end — fallback when no adjacent text is a usable search anchor. */
  | { type: 'end' }
  /** Insert immediately after the `occurrenceIndex`-th match of `value` (the preceding text). */
  | { type: 'after'; value: string; occurrenceIndex: number }
  /** Insert immediately before the `occurrenceIndex`-th match of `value` (the following text). */
  | { type: 'before'; value: string; occurrenceIndex: number };

export interface LocatedDelete {
  kind: 'delete';
  value: string;
  /** 0-based index among identical matches, in document order. */
  occurrenceIndex: number;
  /** Start offset of this op in the baseline string (reference / observability). */
  offset: number;
  /** Defensive backstop; false for real ops since long runs are sliced within the search limit. */
  oversize: boolean;
  /** Span touches a field/bookmark — must abort at pre-flight, never apply. */
  protected: boolean;
}

export interface LocatedInsert {
  kind: 'insert';
  value: string;
  anchor: InsertAnchor;
  offset: number;
  protected: boolean;
}

/**
 * Delete a whole field because a redline fully spans it. Resolved not by search (a field's text
 * isn't searchable) but by the field's live range, addressed by its index into the protected-region
 * set. `protected` is false: this deletion is deliberate, not something to skip.
 */
export interface LocatedFieldDelete {
  kind: 'fieldDelete';
  fieldRegionIndex: number;
  offset: number;
  protected: boolean;
}

export type LocatedOp = LocatedDelete | LocatedInsert | LocatedFieldDelete;

/**
 * Resolve every `delete`/`insert` op to a concrete search target. `keep` ops are consumed only to
 * advance the cursor and to anchor inserts; they produce no `LocatedOp`.
 *
 * @param ops         the op sequence (baseline order) from the diff/explosion stages
 * @param baseline    the visible baseline string the ops were diffed against
 * @param protectedMap field/bookmark guard from T-007
 */
export function locateOps(
  ops: Op[],
  baseline: string,
  protectedMap: ProtectedRegionSet,
): LocatedOp[] {
  const located: LocatedOp[] = [];
  let cursor = 0;

  for (const op of ops) {
    if (op.kind === 'keep') {
      cursor += op.value.length;
      continue;
    }

    if (op.kind === 'delete') {
      const delStart = cursor;
      const delEnd = cursor + op.value.length;
      cursor = delEnd; // delete text exists in the baseline

      // A field that the delete run FULLY spans is deleted too (via its own range) — the user asked
      // for the field gone when a redline covers it. Partly-overlapped fields are left intact.
      protectedMap.regions.forEach((region, fieldRegionIndex) => {
        if (delStart <= region.start && region.end <= delEnd) {
          located.push({ kind: 'fieldDelete', fieldRegionIndex, offset: region.start, protected: false });
        }
      });

      // Edit AROUND fields (T-007): delete the sub-spans of the run that don't overlap a protected
      // (field) region via search. Then split each gap around any UNSEARCHABLE characters (CR/LF,
      // control chars, object anchors) so one bad char never sinks the whole deletion — the text on
      // either side still deletes, the char itself is left. Finally slice to the search limit. So a
      // run wrapping a field, a line break, or exceeding the limit all still apply on the parts that
      // can be searched. Every emitted slice is guaranteed searchable.
      for (const gap of subtractProtected(delStart, delEnd, protectedMap.regions)) {
        for (const seg of splitAtUnsearchable(baseline.slice(gap.start, gap.end), gap.start)) {
          for (const slice of sliceForSearch(seg.text, seg.offset)) {
            // A lone tab/space delete is not locatable (occurrence_missing) and would abort
            // an otherwise valid insert (QR suffix). Leave the marker in the document.
            if (isFormattingOnlyDelete(slice.text)) continue;
            located.push({
              kind: 'delete',
              value: slice.text,
              occurrenceIndex: countOccurrencesBefore(baseline, slice.text, slice.offset),
              offset: slice.offset,
              // Slices are <= MAX_SEARCH_LEN and free of unsearchable chars by construction.
              oversize: slice.text.length > 255,
              protected: protectedMap.overlapsProtected(slice.offset, slice.offset + slice.text.length),
            });
          }
        }
      }
      continue;
    }

    // insert — no baseline width; resolve an anchor from the preceding baseline text.
    //
    // NO bidi pre-swap here. The hybrid engine applies inserts with native `Range.insertText`,
    // which creates a plain run. On Mac Word these runs are NOT given `w:rtl`, so Word does not
    // glyph-mirror paired punctuation `[](){}` in them. Pre-swapping (the legacy insertOoxml
    // behaviour) therefore stored `]…[` and displayed it flipped. Insert the LOGICAL text so the
    // brackets render as authored. (The legacy RedlineInserter path still pre-swaps because it
    // inserts OOXML runs that carry the source run's RTL/complex-script properties.)
    const logicalInsert = op.value;
    if (/[\[\](){}]/.test(logicalInsert)) {
      logQuickResponseBidiCompare('locateOps insert (logical, no swap)', logicalInsert);
    }
    located.push({
      kind: 'insert',
      value: logicalInsert,
      anchor: resolveInsertAnchor(baseline, cursor, protectedMap),
      offset: cursor,
      // Append after the last visible char. A comment range on the last word often
      // extends past visible text (comment mark), which would flag a QR suffix as protected.
      protected:
        cursor >= baseline.length ? false : protectedMap.overlapsProtected(cursor, cursor),
    });
  }

  return located;
}

/**
 * Split a delete value into consecutive slices each within the search limit, so a run longer than
 * Word can search still applies (as several contiguous deletions) instead of aborting. Each slice
 * carries its absolute baseline offset. Slices break at the last whitespace inside the window to
 * stay word-aligned; a single whitespace-free token longer than the window is hard-split. The
 * slices exactly tile the input: `slices.map(s => s.text).join('') === value`.
 */
export function sliceForSearch(
  value: string,
  baseOffset: number,
): Array<{ text: string; offset: number }> {
  if (value.length <= MAX_SEARCH_LEN) return [{ text: value, offset: baseOffset }];

  const slices: Array<{ text: string; offset: number }> = [];
  let i = 0;
  while (i < value.length) {
    let end = Math.min(i + MAX_SEARCH_LEN, value.length);
    if (end < value.length) {
      // Prefer breaking after the last whitespace in [i, end) to keep slices word-aligned.
      let ws = -1;
      for (let k = end - 1; k > i; k--) {
        if (/\s/.test(value[k])) {
          ws = k;
          break;
        }
      }
      if (ws > i) end = ws + 1; // include the whitespace in this slice
    }
    slices.push({ text: value.slice(i, end), offset: baseOffset + i });
    i = end;
  }
  return slices;
}

/**
 * Split `text` into maximal segments that contain NO unsearchable character (CR/LF, control chars,
 * object anchors), dropping the unsearchable characters themselves. Each segment carries its absolute
 * baseline offset. This lets a deletion that spans a line break or stray control char still delete
 * the searchable text on either side (the bad char is simply left in place — harmless).
 */
export function splitAtUnsearchable(
  text: string,
  baseOffset: number,
): Array<{ text: string; offset: number }> {
  const segments: Array<{ text: string; offset: number }> = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (isUnsearchableCharCode(text.charCodeAt(i))) {
      if (i > start) segments.push({ text: text.slice(start, i), offset: baseOffset + start });
      start = i + 1;
    }
  }
  if (start < text.length) segments.push({ text: text.slice(start), offset: baseOffset + start });
  return segments;
}

/**
 * Count non-overlapping occurrences of `value` that start before `offset` — matching how
 * `Range.search` enumerates matches left-to-right. That count is the 0-based occurrence index of the
 * match located at `offset`.
 */
export function countOccurrencesBefore(baseline: string, value: string, offset: number): number {
  if (value === '') return 0;
  let count = 0;
  let from = 0;
  for (;;) {
    const idx = baseline.indexOf(value, from);
    if (idx === -1 || idx >= offset) break;
    count += 1;
    from = idx + value.length; // non-overlapping, as search does
  }
  return count;
}

/**
 * Anchor an insertion to nearby baseline text. Preference order:
 *   1. the text immediately BEFORE the insertion point → insert `after` it;
 *   2. else the text immediately AFTER it → insert `before` it;
 *   3. else append at the paragraph `end`.
 * A genuine offset-0 insertion is `start`. Critically, we never dump a mid/end insertion at the
 * paragraph start just because its preceding text wasn't usable (the bug that put `them.` first).
 */
function resolveInsertAnchor(
  baseline: string,
  cursor: number,
  protectedMap: ProtectedRegionSet,
): InsertAnchor {
  if (cursor === 0) return { type: 'start' };
  // Pure end-append (QR suffix): do not search a long preceding window —
  // comment ranges make Whole.search return an oversized hit that preflight
  // rejects (track_changes_97: anchor_not_found / oversized search hits).
  if (cursor >= baseline.length) return { type: 'end' };

  const preceding = precedingAnchor(baseline, cursor, protectedMap);
  if (preceding) return preceding;

  const following = followingAnchor(baseline, cursor, protectedMap);
  if (following) return following;

  return { type: 'end' };
}

/** The bounded, searchable run of baseline text ending at `cursor` → insert after it. */
function precedingAnchor(
  baseline: string,
  cursor: number,
  protectedMap: ProtectedRegionSet,
): InsertAnchor | null {
  let windowStart = Math.max(0, cursor - MAX_SEARCH_LEN);
  // Do not let the window cross a protected region that ends at/just before the cursor.
  for (const region of protectedMap.regions) {
    if (region.end <= cursor && region.end > windowStart && region.start < cursor) {
      windowStart = Math.max(windowStart, region.end);
    }
  }
  // Start after the last unsearchable char (e.g. a field marker) so the anchor stays a legal term.
  for (let i = cursor - 1; i >= windowStart; i--) {
    if (isUnsearchableCharCode(baseline.charCodeAt(i))) {
      windowStart = i + 1;
      break;
    }
  }
  const value = baseline.slice(windowStart, cursor);
  if (value === '') return null;
  return { type: 'after', value, occurrenceIndex: countOccurrencesBefore(baseline, value, windowStart) };
}

/** The bounded, searchable run of baseline text starting at `cursor` → insert before it. */
function followingAnchor(
  baseline: string,
  cursor: number,
  protectedMap: ProtectedRegionSet,
): InsertAnchor | null {
  let windowEnd = Math.min(baseline.length, cursor + MAX_SEARCH_LEN);
  // Do not let the window cross a protected region that starts at/after the cursor.
  for (const region of protectedMap.regions) {
    if (region.start >= cursor && region.start < windowEnd) {
      windowEnd = Math.min(windowEnd, region.start);
    }
  }
  // End before the first unsearchable char so the anchor stays a legal term.
  for (let i = cursor; i < windowEnd; i++) {
    if (isUnsearchableCharCode(baseline.charCodeAt(i))) {
      windowEnd = i;
      break;
    }
  }
  const value = baseline.slice(cursor, windowEnd);
  if (value === '') return null;
  // The match sits at `cursor`; its occurrence index is how many identical matches precede it.
  return { type: 'before', value, occurrenceIndex: countOccurrencesBefore(baseline, value, cursor) };
}

/**
 * Return the sub-intervals of `[start, end)` that do NOT overlap any protected region — the parts we
 * may edit, in order. Regions must be sorted by start (as `ProtectedRegionSet` guarantees).
 */
export function subtractProtected(
  start: number,
  end: number,
  regions: readonly { start: number; end: number }[],
): Array<{ start: number; end: number }> {
  const gaps: Array<{ start: number; end: number }> = [];
  let pos = start;
  for (const r of regions) {
    if (r.end <= pos) continue; // region entirely before the remaining span
    if (r.start >= end) break; // region entirely after — done
    if (r.start > pos) gaps.push({ start: pos, end: Math.min(r.start, end) });
    pos = Math.max(pos, r.end);
    if (pos >= end) break;
  }
  if (pos < end) gaps.push({ start: pos, end });
  return gaps.filter((g) => g.end > g.start);
}
